/**
 * Offline session write queue backed by IndexedDB (idb-keyval).
 * pwa-offlineSKILL-final.md — File 6.
 *
 * Rules:
 *  3. Flushes to Supabase automatically on reconnect — no data loss
 *  9. Only userId stored — no other PII (COPPA)
 * 10. Uses idb-keyval, never localStorage
 */
import { get, set, del, keys } from 'idb-keyval'
import { supabase }             from '@/lib/supabase'

interface QueuedSessionWrite {
  id:          string    // uuid
  userId:      string    // Rule 9 — only PII stored
  sessionData: {
    subject:       string
    topic:         string
    gradeLevel:    number
    mode:          string   // 'practice' | 'challenge'
    difficulty:    string   // 'easy' | 'medium' | 'hard'
    correctCount:  number
    questionsCount: number
    durationSeconds: number
  }
  queuedAt:    string    // ISO timestamp
  attempts:    number
}

const QUEUE_PREFIX  = 'offline_session_'
const MAX_ATTEMPTS  = 5

let isFlushing = false

// ── Enqueue a session write ───────────────────────────────────────────────────

export async function enqueueSessionWrite(
  userId:      string,
  sessionData: QueuedSessionWrite['sessionData'],
): Promise<void> {
  const id   = crypto.randomUUID()
  const item: QueuedSessionWrite = {
    id, userId, sessionData,
    queuedAt: new Date().toISOString(),
    attempts: 0,
  }
  await set(`${QUEUE_PREFIX}${id}`, item)
}

// ── Flush queue to Supabase ───────────────────────────────────────────────────

export async function flushOfflineQueue(): Promise<{ flushed: number; failed: number }> {
  if (isFlushing) return { flushed: 0, failed: 0 }
  isFlushing = true

  try {
    const allKeys    = await keys()
    const queuedKeys = (allKeys as string[]).filter((k) => k.startsWith(QUEUE_PREFIX))

    let flushed = 0
    let failed  = 0

    for (const key of queuedKeys) {
      const item = await get<QueuedSessionWrite>(key)
      if (!item) continue

      try {
        const { error } = await supabase.from('sessions').insert({
          student_id:       item.userId,
          subject:          item.sessionData.subject,
          topic:            item.sessionData.topic,
          grade_level:      item.sessionData.gradeLevel,
          mode:             item.sessionData.mode,
          difficulty:       item.sessionData.difficulty,
          correct_count:    item.sessionData.correctCount,
          questions_count:  item.sessionData.questionsCount,
          duration_seconds: item.sessionData.durationSeconds,
          started_at:       item.queuedAt,
        })
        if (error) throw error
        await del(key)
        flushed++
      } catch {
        const updated = { ...item, attempts: item.attempts + 1 }
        if (updated.attempts >= MAX_ATTEMPTS) {
          await del(key)
          failed++
        } else {
          await set(key, updated)
        }
      }
    }

    return { flushed, failed }
  } finally {
    isFlushing = false
  }
}

// ── Pending count (for optional UI badge) ────────────────────────────────────

export async function getPendingWriteCount(): Promise<number> {
  const allKeys = await keys()
  return (allKeys as string[]).filter((k) => k.startsWith(QUEUE_PREFIX)).length
}
